// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import { watch } from 'vue';
import { auth } from '@/firebaseConfig'; 
import { useUserStore } from '@/stores/userStore';

import LandingView from '../views/LandingView.vue';
import LoginView from '../views/LoginView.vue';
import RegisterView from '../views/RegisterView.vue';
import DashboardView from '../views/DashboardView.vue';
import NewDashboardView from '../views/NewDashboardView.vue';
import OnboardingMaterialView from '../views/OnBoarding/OnboardingMaterialView.vue';
import OnboardingFormsView from '../views/OnBoarding/OnboardingFormsView.vue';
import YourDetailsFormView from '../views/OnBoarding/YourDetailsFormView.vue';
import EmployeeInductionFormView from '../views/OnBoarding/EmployeeInductionFormView.vue';
import RolesResponsibilitiesFormView from '../views/OnBoarding/RolesResponsibilitiesFormView.vue';
import LiteracyTestFormView from '../views/OnBoarding/LiteracyTestFormView.vue';
import PrimarySponsorContractFormView from '../views/OnBoarding/PrimarySponsorContractFormView.vue';
import AnnualHealthAssessmentFormView from '../views/OnBoarding/AnnualHealthAssessmentFormView.vue';
import EyesightDeclarationFormView from '../views/OnBoarding/EyesightDeclarationFormView.vue';
import RightToWorkFormView from '../views/OnBoarding/RightToWorkFormView.vue';
import PpeRequestFormView from '../views/OnBoarding/PpeRequestFormView.vue';
import EmployeeHandbookSignOffFormView from '../views/OnBoarding/EmployeeHandbookSignOffFormView.vue';
import SubContractorOnboardingView from '../views/OnBoarding/SubContractorOnboardingView.vue';
import UserProfileView from '../views/UserProfileView.vue';
import CompletedFormView from '../views/CompletedFormView.vue';
import AdminOnboardingDashboardView from '../views/admin/AdminOnboardingDashboardView.vue';
import AdminHealthSafetyDashboardView from '../views/admin/AdminHealthSafetyDashboardView.vue';
import AdminOnboardingManageFormOptionsView from '../views/admin/AdminOnboardingManageFormOptionsView.vue';
import AdminOnboardingManageFormTextView from '../views/admin/AdminOnboardingManageFormTextView.vue';
import AdminHealthSafetyManageFormOptionsView from '../views/admin/AdminHealthSafetyManageFormOptionsView.vue';
import AdminHealthSafetyManageFormTextView from '../views/admin/AdminHealthSafetyManageFormTextView.vue';
import AdminCompletedFormsListView from '../views/admin/AdminCompletedFormsListView.vue';
import AdminGeneratedPdfsView from '../views/admin/AdminGeneratedPdfsView.vue';
import AdminUserProfileView from '../views/admin/AdminUserProfileView.vue';
import AdminAnalyticsDashboardView from '../views/admin/AdminAnalyticsDashboardView.vue';
import AdminUsefulLinksView from '../views/admin/AdminUsefulLinksView.vue';
import AccidentWitnessStatementFormView from '../views/HealthandSafety/AccidentWitnessStatementFormView.vue';
import IncidentInvestigationFormView from '../views/HealthandSafety/IncidentInvestigationFormView.vue';
import SafetyHistoryView from '../views/HealthandSafety/SafetyHistoryView.vue'; 
import UsefulLinksView from '../views/UsefulLinksView.vue';
import ComingSoonView from '../views/ComingSoonView.vue';
import AdminTrainingDashboardView from '../views/admin/AdminTrainingDashboardView.vue';
import FatigueManageFormTextView from '../views/fatigue/FatigueManageFormTextView.vue';
import AdminFatigueManageFormOptionsView from '../views/fatigue/AdminFatigueManageFormOptionsView.vue';
import AdminIssueReportFormView from '../views/admin/AdminIssueReportFormView.vue';
import AdminIssueReportDetailView from '../views/admin/AdminIssueReportDetailView.vue';
import AdminFeatureRequestFormView from '../views/admin/AdminFeatureRequestFormView.vue';
import AdminFeatureRequestDetailView from '../views/admin/AdminFeatureRequestDetailView.vue';
import AdminManageRolesView from '../views/admin/AdminManageRolesView.vue';
import AdminPersonnelDashboardView from '../views/admin/AdminPersonnelDashboardView.vue';
import SupportRequestFormView from '../views/support-requests/SupportRequestFormView.vue';
import AdminEngineeringDashboardView from '../views/admin/AdminEngineeringDashboardView.vue';
import AdminEngineeringManageFormOptionsView from '../views/admin/AdminEngineeringManageFormOptionsView.vue';
import AdminEngineeringManageFormTextView from '../views/admin/AdminEngineeringManageFormTextView.vue';
import AdminManageDocumentsView from '../views/admin/AdminManageDocumentsView.vue';
import DocumentView from '../views/documents/DocumentView.vue';
import AdminDocumentEditorView from '../views/admin/AdminDocumentEditorView.vue';
import PublishedDocumentsView from '../views/documents/PublishedDocumentsView.vue';
import AdminManageVehiclesView from '../views/admin/AdminManageVehiclesView.vue';
import AdminVehicleEditorView from '../views/admin/vehicles/AdminVehicleEditorView.vue';
import VehicleCheckFormView from '../views/vehicles/VehicleCheckFormView.vue';
import VehicleDetailsView from '../views/vehicles/VehicleDetailsView.vue';
import EngineeringProjectsView from '../views/engineering/EngineeringProjectsView.vue'; 
import AdminProjectEditorView from '../views/admin/engineering/AdminProjectEditorView.vue';
import ProjectDetailView from '../views/engineering/projects/ProjectDetailView.vue';
import AdminTaskListView from '../views/admin/AdminTaskListView.vue';
import AdminFinanceDashboardView from '../views/admin/AdminFinanceDashboardView.vue';
import TimesheetHistoryView from '../views/timesheets/TimesheetHistoryView.vue';

// Plant & Equipment / Asset Registry (MSPRG009 / MSPRG010)
import PlantEquipmentRegisterView from '../views/projects/PlantEquipmentRegisterView.vue';
import SingleItemInspectionFormView from '../views/projects/SingleItemInspectionFormView.vue';
import MultiItemInspectionFormView from '../views/projects/MultiItemInspectionFormView.vue';

// Timesheets
import TimesheetEntryView from '../views/timesheets/TimesheetEntryView.vue';
import ManagerTimesheetReviewView from '../views/timesheets/ManagerTimesheetReviewView.vue';

// Reviews
import UserQrCodeView from '../views/reviews/UserQrCodeView.vue';
import PublicUserReviewFormView from '../views/reviews/PublicUserReviewFormView.vue';
import ClientFeedbackFormView from '../views/reviews/ClientFeedbackFormView.vue'; 
import UserReviewsListView from '../views/reviews/UserReviewsListView.vue';

// Lazy load form components for public routes
const HealthSafetyFormsView = () => import('../views/HealthandSafety/HealthSafetyFormsView.vue');
const ObservationFormView = () => import('../views/HealthandSafety/ObservationFormView.vue');
const AccidentFormView = () => import('../views/HealthandSafety/AccidentFormView.vue');

const routes = [
  {
    path: '/',
    name: 'home',
    component: LandingView,
    meta: { layout: 'Blank' }
  },
  {
    path: '/system-lockout',
    name: 'SystemLockout',
    component: () => import('../views/SystemLockoutView.vue'),
    meta: { layout: 'Blank', title: 'System Lockout' }
  },
  {
    path: '/support-request/:formSlug',
    name: 'supportRequestForm',
    component: SupportRequestFormView,
    props: true,
    meta: { hideSubHeader: true }
  },
  {
    path: '/support-request/:formSlug/:id',
    name: 'publicSupportRequestDetail',
    component: CompletedFormView,
    props: true,
    meta: { hideSubHeader: true }
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterView
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: DashboardView,
    meta: { requiresAuth: true, title: 'Dashboard' }
  },
  {
    path: '/new-dashboard',
    name: 'newDashboard',
    component: NewDashboardView,
    meta: { requiresAuth: true, title: 'Dashboard' }
  },
  {
    path: '/health-safety-material',
    name: 'healthSafetyMaterial',
    component: ComingSoonView,
    meta: { requiresAuth: true, title: 'H&S Material' }
  },
  {
    path: '/training-material',
    name: 'TrainingMaterial',
    component: () => import('../views/training/TrainingMaterialView.vue'),
    meta: { requiresAuth: true, title: 'Training Materials' }
  },
  {
    path: '/training-request',
    name: 'trainingRequest',
    component: ComingSoonView,
    meta: { requiresAuth: true, title: 'Training Request' }
  },
  {
    path: '/admin/training-options',
    name: 'AdminTrainingManageFormOptions',
    component: () => import('@/views/admin/AdminTrainingManageFormOptionsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  
  // --- TIMESHEETS ---
  {
    path: '/timesheets/new',
    name: 'TimesheetEntry',
    component: TimesheetEntryView,
    meta: { requiresAuth: true, title: 'Log Weekly Hours' }
  },
  {
    path: '/timesheets/history',
    name: 'TimesheetHistory',
    component: TimesheetHistoryView,
    meta: { requiresAuth: true, title: 'Timesheet History' }
  },
  {
    path: '/timesheets/review',
    name: 'ManagerTimesheetReview',
    component: ManagerTimesheetReviewView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Timesheet Authorisation' }
  },
  {
    path: '/admin/manage-finance-form-options',
    name: 'AdminFinanceManageFormOptions',
    component: () => import('@/views/admin/AdminFinanceManageFormOptionsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },

  // --- REVIEWS ---
  {
    path: '/review-me',
    name: 'UserReviewQr',
    component: UserQrCodeView,
    meta: { requiresAuth: true, title: 'Review Me' }
  },
  {
    path: '/user/:userId/reviews', 
    name: 'UserReviewsList',
    component: UserReviewsListView,
    props: true,
    meta: { requiresAuth: true, title: 'User Reviews' }
  },
  {
    path: '/public/review/:userId',
    name: 'PublicUserReview',
    component: PublicUserReviewFormView,
    meta: { layout: 'Blank', title: 'Leave a Review' }
  },
  {
    path: '/public/client-feedback/:userId',
    name: 'PublicClientFeedback',
    component: ClientFeedbackFormView,
    meta: { layout: 'Blank', title: 'Client Feedback Questionnaire' }
  },

  // --- PLANT & EQUIPMENT / ASSET REGISTER (MSPRG009 & MSPRG010) ---
  {
    path: '/plant/register',
    name: 'PlantEquipmentRegister',
    component: PlantEquipmentRegisterView,
    meta: { requiresAuth: true, title: 'Plant & Equipment Register' }
  },
  {
    path: '/plant/inspection/single/:assetId?',
    name: 'SingleItemInspectionForm',
    component: SingleItemInspectionFormView,
    props: true,
    meta: { requiresAuth: true, title: 'Pre-Use Inspection (Single Item)' }
  },
  {
    path: '/plant/inspection/multi',
    name: 'MultiItemInspectionForm',
    component: MultiItemInspectionFormView,
    meta: { requiresAuth: true, title: 'Pre-Use Inspection (Multi-Item)' }
  },

  // --- ENGINEERING ---
  {
    path: '/engineering/projects',
    name: 'EngineeringProjects',
    component: EngineeringProjectsView, 
    meta: { requiresAuth: true, title: 'Engineering Projects' }
  },
  {
    path: '/engineering/projects/new',
    name: 'CreateProject',
    component: AdminProjectEditorView,
    meta: { requiresAuth: true, title: 'Create Project' }
  },
  {
    path: '/engineering/projects/:id',
    name: 'ProjectDetails',
    component: ProjectDetailView,
    meta: { requiresAuth: true, title: 'Project Details' }
  },
  {
    path: '/engineering/projects/edit/:id',
    name: 'EditProject',
    component: AdminProjectEditorView,
    meta: { requiresAuth: true, title: 'Edit Project' }
  },
  {
    path: '/engineering/forms',
    name: 'engineeringForms',
    component: ComingSoonView,
    meta: { requiresAuth: true, title: 'Engineering Forms' }
  },

  // --- VEHICLES ---
  {
    path: '/vehicle-details',
    name: 'vehicleDetails',
    component: VehicleDetailsView,
    meta: { requiresAuth: true, title: 'My Vehicle' }
  },
  {
    path: '/vehicle-check',
    name: 'vehicleCheck',
    component: VehicleCheckFormView,
    meta: { requiresAuth: true, title: 'Vehicle Check' }
  },
  {
    path: '/admin/fleet-audit',
    name: 'AdminVehicleCheckHistory',
    component: () => import('@/views/admin/vehicles/AdminVehicleCheckHistoryView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },

  // --- DOCUMENTS ---
  {
    path: '/documents',
    name: 'PublishedDocuments',
    component: PublishedDocumentsView,
    meta: { requiresAuth: true, title: 'Company Documents' }
  },
  {
    path: '/document/:documentId',
    name: 'ViewDocument',
    component: DocumentView,
    props: true,
    meta: { requiresAuth: true, title: 'Company Document' }
  },
  {
    path: '/useful-links',
    name: 'UsefulLinks',
    component: UsefulLinksView,
    meta: { requiresAuth: true, title: 'Useful Links' }
  },

  // --- ONBOARDING ---
  {
    path: '/onboarding-material',
    name: 'onboardingMaterial',
    component: OnboardingMaterialView,
    meta: { requiresAuth: true, title: 'Onboarding Material' }
  },
  {
    path: '/onboarding-forms',
    name: 'onboardingForms',
    component: OnboardingFormsView,
    meta: { requiresAuth: true, title: 'Onboarding Forms' }
  },
  {
    path: '/forms/sub-contractor-pack',
    name: 'SubContractorPackForm',
    component: SubContractorOnboardingView, 
    meta: { requiresAuth: true, title: 'Sub-Contractor Pack' }
  },
  {
    path: '/public/onboarding/:id',
    name: 'PublicSubContractorOnboarding',
    component: SubContractorOnboardingView,
    props: true,
    meta: { layout: 'Blank', title: 'Onboarding Pack' }
  },
  {
    path: '/forms/your-details',
    name: 'yourDetailsForm',
    component: YourDetailsFormView,
    meta: { requiresAuth: true, title: 'Your Details' }
  },
  {
    path: '/forms/employee-induction',
    name: 'employeeInductionForm',
    component: EmployeeInductionFormView,
    meta: { requiresAuth: true, title: 'Employee Induction' }
  },
  {
    path: '/forms/roles-responsibilities',
    name: 'rolesAndResponsibilitiesForm',
    component: RolesResponsibilitiesFormView,
    meta: { requiresAuth: true, title: 'Roles & Responsibilities' }
  },
  {
    path: '/forms/literacy-test',
    name: 'literacyTestForm',
    component: LiteracyTestFormView,
    meta: { requiresAuth: true, title: 'Literacy Test' }
  },
  {
    path: '/forms/primary-sponsor-contract',
    name: 'primarySponsorContractForm',
    component: PrimarySponsorContractFormView,
    meta: { requiresAuth: true, title: 'Primary Sponsor Contract' }
  },
  {
    path: '/forms/annual-health-assessment',
    name: 'annualHealthAssessmentForm',
    component: AnnualHealthAssessmentFormView,
    meta: { requiresAuth: true, title: 'Annual Health Assessment' }
  },
  {
    path: '/forms/eyesight-declaration',
    name: 'eyesightDeclarationForm',
    component: EyesightDeclarationFormView,
    meta: { requiresAuth: true, title: 'Eyesight Declaration' }
  },
  {
    path: '/forms/right-to-work',
    name: 'rightToWorkForm',
    component: RightToWorkFormView,
    meta: { requiresAuth: true, title: 'Right To Work' }
  },
  {
    path: '/forms/ppe-request',
    name: 'ppeRequestForm',
    component: PpeRequestFormView,
    meta: { requiresAuth: true, title: 'PPE Request' }
  },
  {
    path: '/forms/employee-handbook-sign-off',
    name: 'employeeHandbookSignOffForm',
    component: EmployeeHandbookSignOffFormView,
    meta: { requiresAuth: true, title: 'Employee Handbook Sign-Off' }
  },
  {
    path: '/profile',
    name: 'userProfile',
    component: UserProfileView,
    meta: { requiresAuth: true, title: 'My Profile' }
  },
  {
    path: '/completed-form/:collectionName/:submissionId',
    name: 'completedForm',
    component: CompletedFormView,
    props: true,
    meta: { requiresAuth: true, title: 'Completed Form' }
  },
  
  // 🛡️ RE-ALIGNED NOTIFICATION TARGET ROUTE: Direct alias map for legacy notification linkages
  {
    path: '/admin/submissions/:collectionName/:submissionId',
    name: 'adminCompletedForm',
    component: CompletedFormView,
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Completed Form' }
  },

  // --- HEALTH & SAFETY ---
  {
    path: '/health-safety-forms',
    name: 'HealthSafetyFormsList',
    component: HealthSafetyFormsView,
    meta: { title: 'Health & Safety Forms' }
  },
  {
    path: '/health-safety/history', 
    name: 'SafetyHistory',
    component: SafetyHistoryView,
    meta: { requiresAuth: true, title: 'Safety Submissions' }
  },
  {
    path: '/health-safety/observation-form',
    name: 'ObservationForm',
    component: ObservationFormView,
    meta: { requiresAuth: true, title: 'Observation Form' }
  },
  {
    path: '/health-safety/accident-form',
    name: 'AccidentForm',
    component: AccidentFormView,
    meta: { requiresAuth: true, title: 'Accident Form' }
  },
  {
    path: '/health-safety/accident-witness-statement-form',
    name: 'AccidentWitnessStatementForm',
    component: AccidentWitnessStatementFormView,
    meta: { requiresAuth: true, title: 'Accident Witness Statement' }
  },
  {
    path: '/health-safety/investigation-registry',
    name: 'HSInvestigationRegistry',
    component: IncidentInvestigationFormView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'H&S Investigation Registry' }
  },
  {
    path: '/health-safety/worksite-inspection-form',
    name: 'WorksiteInspectionForm',
    component: () => import('../views/HealthandSafety/WorksiteInspectionFormView.vue'),
    meta: { requiresAuth: true, title: 'Worksite Inspection' }
  },
  {
    path: '/health-safety/coss-communications-monitoring',
    name: 'CossCommunicationsMonitoring',
    component: () => import('../views/HealthandSafety/CossCommunicationsMonitoringView.vue'),
    meta: { requiresAuth: true, title: 'COSS Communications Monitoring' }
  },

  // --- PUBLIC CLIENT ROUTES ---
  {
    path: '/public/:clientId/health-safety',
    name: 'PublicHealthSafetyFormsList',
    component: HealthSafetyFormsView,
    props: true,
    meta: { title: 'Health & Safety' }
  },
  {
    path: '/public/:clientId/observation-form',
    name: 'PublicObservationForm',
    component: ObservationFormView,
    props: true,
    meta: { title: 'Observation Form' }
  },
  {
    path: '/public/:clientId/accident-form',
    name: 'PublicAccidentForm',
    component: AccidentFormView,
    props: true,
    meta: { title: 'Accident Form' }
  },
  {
    path: '/public/:clientId/witness-statement',
    name: 'PublicAccidentWitnessStatementForm',
    component: AccidentWitnessStatementFormView,
    props: true,
    meta: { title: 'Witness Statement' }
  },

  // --- ADMIN ROUTES ---
  {
    path: '/client-admin',
    name: 'ClientAdminDashboard',
    component: () => import('../views/client-admin/ClientAdminDashboardView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Client Administration' }
  },
  {
    path: '/admin/personnel',
    name: 'AdminPersonnelDashboard',
    component: AdminPersonnelDashboardView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Personnel Command Centre' }
  },
  {
    path: '/super-admin',
    name: 'SuperAdminDashboard',
    component: () => import('../views/super-admin/SuperAdminDashboardView.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true, title: 'Super Admin' }
  },
  {
    path: '/admin/manage-documents',
    name: 'AdminManageDocuments',
    component: AdminManageDocumentsView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage Documents' }
  },
  {
    path: '/admin/documents/new',
    name: 'CreateDocument',
    component: AdminDocumentEditorView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Create Document' }
  },
  {
    path: '/admin/documents/edit/:id',
    name: 'EditDocument',
    component: AdminDocumentEditorView,
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Edit Document' }
  },
  {
    path: '/admin/tasks', 
    name: 'AdminTaskList',
    component: AdminTaskListView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Task Management' }
  },
  {
    path: '/admin/onboarding-records',
    name: 'AdminOnboardingRecords',
    component: AdminOnboardingDashboardView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Onboarding Records' }
  },
  {
    path: '/admin/engineering',
    name: 'AdminEngineeringDashboard',
    component: AdminEngineeringDashboardView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Engineering' }
  },
  {
    path: '/admin/engineering/manage-options',
    name: 'AdminEngineeringManageFormOptions',
    component: AdminEngineeringManageFormOptionsView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage Engineering Options' }
  },
  {
    path: '/admin/engineering/manage-text',
    name: 'AdminEngineeringManageFormText',
    component: AdminEngineeringManageFormTextView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage Engineering Text' }
  },
  {
    path: '/admin/fatigue-planner',
    name: 'AdminFatiguePlanner',
    component: () => import('../views/admin/AdminFatigueDashboardView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Fatigue Management' }
  },
  {
    path: '/admin/training',
    name: 'AdminTrainingDashboard',
    component: AdminTrainingDashboardView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Training Management' }
  },
  {
    path: '/admin/vehicles',
    name: 'AdminManageVehicles',
    component: AdminManageVehiclesView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Fleet Management' }
  },
  {
    path: '/admin/vehicles/edit/:id',
    name: 'AdminVehicleEditor',
    component: AdminVehicleEditorView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Edit Vehicle' }
  },
  {
    path: '/admin/vehicles-placeholder',
    name: 'AdminVehiclesPlaceholder',
    component: ComingSoonView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Vehicle Management' }
  },
  {
    path: '/admin/finance',
    name: 'AdminFinance',
    component: AdminFinanceDashboardView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Finance & Payroll' }
  },
  {
    path: '/admin/useful-links',
    name: 'AdminManageResources', 
    component: AdminUsefulLinksView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage Useful Links' }
  },
  {
    path: '/admin/coming-soon-1',
    name: 'ComingSoon1',
    component: ComingSoonView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Coming Soon' }
  },
  {
    path: '/admin/coming-soon-2',
    name: 'ComingSoon2',
    component: ComingSoonView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Coming Soon' }
  },
  {
    path: '/admin/training/toolbox-talks',
    name: 'AdminToolboxTalksList',
    component: () => import('../views/admin/training/AdminToolboxTalksListView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Toolbox Talks' }
  },
  {
    path: '/admin/training/toolbox-talk/:talkId',
    name: 'AdminToolboxTalkDetail',
    component: () => import('../views/admin/training/AdminToolboxTalkDetailView.vue'),
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Toolbox Talk Details' }
  },
  {
    path: '/admin/training/toolbox-talks/edit/:talkId',
    name: 'AdminToolboxTalkEdit',
    component: () => import('../views/admin/training/AdminToolboxTalkEditView.vue'),
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Edit Toolbox Talk' }
  },
  {
    path: '/toolbox-talks/sign/:talkId',
    name: 'ToolboxTalkConfirmation',
    component: () => import('../views/training/ToolboxTalkConfirmationView.vue'),
    props: true,
    meta: { requiresAuth: true, title: 'Toolbox Talk Authorisation' }
  },
  {
    path: '/fatigue',
    name: 'FatigueDashboard',
    component: () => import('../views/admin/AdminFatigueDashboardView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Fatigue Management' }
  },
  {
    path: '/fatigue/assessment',
    name: 'FatigueAssessment',
    component: () => import('../views/fatigue/FatigueAssessmentView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Fatigue Assessment' }
  },
  {
    path: '/fatigue/management-plan',
    name: 'FatigueManagementPlan',
    component: () => import('../views/fatigue/FatigueManagementPlanView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Fatigue Management Plan' }
  },
  {
    path: '/fatigue/risk-assessment',
    name: 'FatigueRiskAssessment',
    component: () => import('../views/fatigue/FatigueRiskAssessmentView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Fatigue Risk Assessment' }
  },
  {
    path: '/fatigue/shifts/new',
    name: 'CreateShift',
    component: () => import('../views/fatigue/CreateShiftFormView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Create Shift' }
  },
  {
    path: '/fatigue/planner',
    name: 'FatiguePlanner',
    component: () => import('../views/fatigue/FatiguePlannerView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Fatigue Planner' }
  },
  {
    path: '/admin/manage-fatigue-form-text',
    name: 'AdminFatigueManageFormText',
    component: FatigueManageFormTextView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage Fatigue Text' }
  },
  {
    path: '/admin/manage-fatigue-form-options',
    name: 'AdminFatigueManageFormOptions',
    component: AdminFatigueManageFormOptionsView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage Fatigue Options' }
  },
  {
    path: '/admin/report-issue',
    name: 'IssueReportForm',
    component: AdminIssueReportFormView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Report an Issue' }
  },
  {
    path: '/admin/issue-report/:reportId',
    name: 'AdminIssueReportDetail',
    component: AdminIssueReportDetailView,
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Issue Report Details' }
  },
  {
    path: '/admin/feature-request/:requestId',
    name: 'AdminFeatureRequestDetail',
    component: AdminFeatureRequestDetailView,
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Feature Request Details' }
  },
  {
    path: '/admin/request-feature',
    name: 'FeatureRequestForm',
    component: AdminFeatureRequestFormView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Request a Feature' }
  },
  {
    path: '/admin/roles',
    name: 'AdminManageRoles',
    component: AdminManageRolesView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage Roles' }
  },
  {
    path: '/admin/users/new',
    name: 'AdminAddUser',
    component: YourDetailsFormView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Add New User' }
  },
  {
    path: '/admin/user/:userId',
    name: 'AdminUserProfile',
    component: AdminUserProfileView,
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'User Profile' }
  },
  {
    path: '/admin/user/edit/:userId',
    name: 'AdminEditUser',
    component: YourDetailsFormView,
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Edit User Details' }
  },
  {
    path: '/admin/analytics',
    name: 'AdminAnalyticsDashboard',
    component: AdminAnalyticsDashboardView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Analytics Dashboard' }
  },
  {
    path: '/admin/form-records',
    name: 'AdminOnboardingDashboard',
    component: AdminOnboardingDashboardView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Onboarding Records' }
  },
  {
    path: '/admin/health-safety-records',
    name: 'AdminHealthSafetyDashboard',
    component: AdminHealthSafetyDashboardView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'H&S Records' }
  },
  {
    path: '/admin/manage-onboarding-form-options',
    name: 'AdminOnboardingManageFormOptions',
    component: AdminOnboardingManageFormOptionsView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage Onboarding Options' }
  },
  {
    path: '/admin/manage-onboarding-form-text',
    name: 'AdminOnboardingManageFormTextView',
    component: AdminOnboardingManageFormTextView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage Onboarding Text' }
  },
  {
    path: '/admin/manage-health-safety-form-options',
    name: 'AdminHealthSafetyManageFormOptions',
    component: AdminHealthSafetyManageFormOptionsView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage H&S Options' }
  },
  {
    path: '/admin/manage-health-safety-form-text',
    name: 'AdminHealthSafetyManageFormTextView',
    component: AdminHealthSafetyManageFormTextView,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage H&S Text' }
  },
  {
    path: '/admin/records/list/:formIdentifier',
    name: 'AdminCompletedFormsList',
    component: AdminCompletedFormsListView,
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Completed Records' }
  },
  {
    path: '/admin/pdf-reports/:formIdentifier',
    name: 'AdminGeneratedPdfs',
    component: AdminGeneratedPdfsView,
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Generated PDFs' }
  },

  // --- AUDIT COMPLIANCE ROUTES ---
  {
    path: '/admin/audit/iso-45001',
    name: 'AdminIso45001Audit',
    component: () => import('../views/admin/audit/AdminIso45001AuditView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'ISO 45001 Audit Hub' }
  },
  {
    path: '/admin/audit/risqs',
    name: 'AdminRisqsAudit',
    component: () => import('../views/admin/audit/AdminRisqsAuditView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'RISQS Compliance Board' }
  },
  {
    path: '/admin/audit/sample-vault',
    name: 'AdminIso19011SamplingVault',
    component: () => import('../views/admin/audit/AdminIso19011SamplingVaultView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'ISO 19011 Sampling Vault' }
  },

  // 🛡️ UNRESTRICTED STATELESS PUBLIC AUDIT ENDPOINT
  {
    path: '/public/audit/exhibit-flipbook',
    name: 'PublicAuditFlipbook',
    component: () => import('../views/admin/audit/AdminIso19011SamplingVaultView.vue'),
    meta: { layout: 'Blank', title: 'Accredited Audit Flipbook Brochure' }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, behavior: 'smooth' };
    }
  }
});

/**
 * 🛡️ PHASE 18 NAVIGATION GUARD: FUNCTIONAL AUTHORITY GATE
 */
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();

  // 🛡️ PDF BYPASS HANDSHAKE
  if (to.query.pdfToken === 'SS_PDF_GENERATOR_9922') {
    return next();
  }

  // Stateless public routes that never require session tracking or user profile hydration
  const statelessPublicRoutes = [
    'supportRequestForm',
    'publicSupportRequestDetail',
    'PublicSubContractorOnboarding',
    'PublicUserReview',
    'PublicClientFeedback',
    'PublicObservationForm',
    'PublicAccidentForm',
    'PublicAccidentWitnessStatementForm',
    'PublicHealthSafetyFormsList',
    'PublicAuditFlipbook'
  ];

  if (statelessPublicRoutes.includes(to.name)) {
      return next();
  }

  // 🛡️ CRITICAL HYDRATION WAIT: Wait for Auth and User Profile to synchronize
  if (!userStore.isAuthReady || userStore.isUserProfileLoading) {
    await new Promise((resolve) => {
      const unwatch = watch(
        () => [userStore.isAuthReady, userStore.isUserProfileLoading],
        ([ready, loading]) => {
          if (ready && !loading) {
            unwatch();
            resolve();
          }
        }
      );
    });
  }

  const currentUser = auth.currentUser;
  const isSuperAdmin = userStore.role === 'super-admin';
  
  // Real-time bypass variables, initialized to current store values as fallbacks
  const isMaintenance = userStore.isMaintenanceMode;
  const isClosed = userStore.isClientClosed;

  // 🛡️ THE GLOBAL LOCKOUT GATE (Fast, synchronous, relies on real-time store updates!)
  if ((isMaintenance || isClosed) && !isSuperAdmin && to.name !== 'SystemLockout') {
      const reason = isMaintenance ? 'maintenance' : 'client_closed';
      return next({ name: 'SystemLockout', query: { reason } });
  }

  // Protocol Auto-Recovery
  if (to.name === 'SystemLockout' && !isMaintenance && !isClosed && !isSuperAdmin) {
      return next({ name: 'dashboard' }); 
  }

  // --- AUTOMATIC SESSION FORWARDING HANDSHAKE ---
  // If the user has an active authenticated session and navigates to the landing or login pages,
  // we immediately bypass the guest forms and forward them directly to their active Dashboard.
  const hostname = window.location.hostname;
  const isCoUkDomain = hostname.includes('sentinel-safe.co.uk');

  if (currentUser && (to.name === 'login' || to.name === 'register' || to.name === 'home') && !isCoUkDomain) {
    return next({ name: 'dashboard' });
  }

  // Allow unauthenticated guest navigation to remaining public views (landing page, etc.)
  if (to.name === 'home' || to.name === 'login' || to.name === 'register') {
    return next();
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
  const requiresSuperAdmin = to.matched.some(record => record.meta.requiresSuperAdmin);

  // Authentication Handshake
  if ((requiresAuth || requiresAdmin || requiresSuperAdmin) && !currentUser) {
    return next({ name: 'login', query: { redirect: to.fullPath } });
  }

  // 🛡️ AUTHORITATIVE PERMISSION HANDSHAKE
  if (currentUser && (requiresAdmin || requiresSuperAdmin)) {
    const permissions = userStore.claims?.permissions || {};

    // Super Admin Gateway
    if (requiresSuperAdmin) {
        if (isSuperAdmin) return next();
        return next({ name: 'dashboard' });
    } 
    
    if (requiresAdmin) {
      /** 🛡️ THE PERMISSION HANDSHAKE */
      const isAuthorisedOfficer = userStore.isAdmin || 
                                 userStore.hasAdminAccess || 
                                 userStore.isManager || 
                                 userStore.isDirector;

      if (isAuthorisedOfficer) {
          return next();
      }
      
      // Secondary Route-Specific Clearance Tiers
      if (to.name === 'AdminHealthSafetyManageFormOptions' || to.name === 'AdminHealthSafetyManageFormText') {
        if (permissions.canManageHSQEForms || permissions.canViewHealthAndSafety) return next();
      }
      if (to.name === 'AdminOnboardingManageFormOptions' || to.name === 'AdminOnboardingManageFormText') {
        if (permissions.canManageOnboarding || permissions.canViewOnboarding) return next();
      }
      if (to.name === 'AdminFatigueManageFormOptions' || to.name === 'AdminFatigueManageFormText') {
          if (permissions.canViewFatigue) return next();
      }

      const strictAdminOnlyRoutes = ['AdminManageRoles', 'HSInvestigationRegistry'];
      if (strictAdminOnlyRoutes.includes(to.name)) {
        if (permissions.canManageUsers || permissions.canViewUsers) return next();
      }

      return next({ name: 'dashboard' });
    }
  }

  return next();
});

export default router;